import { useState } from "react";
import { X } from "@phosphor-icons/react";
import { PRIORITY_CONFIG } from "@/lib/constants";
import { LLSelect } from "@/components/ui/ll-select";

export default function JobFormDialog({ assets, onClose, onSave }) {
  const [form, setForm] = useState({
    customer: "",
    customer_email: "",
    pickup_address: "",
    dropoff_address: "",
    scheduled_at: new Date().toISOString().slice(0, 16),
    priority: "medium",
    asset_id: "",
    weight_lbs: 0,
    notes: "",
  });

  const update = (key, val) => setForm({ ...form, [key]: val });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      asset_id: form.asset_id || null,
      customer_email: (form.customer_email || "").trim() || null,
      scheduled_at: new Date(form.scheduled_at).toISOString(),
      weight_lbs: parseInt(form.weight_lbs) || 0,
    });
  };

  const trucks = assets.filter((a) => a.type === "truck");

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4" data-testid="job-form-dialog">
      <div className="bg-[#0d0d0d] border border-[#4d4d4d] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-[#2a2a2a] p-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">New Dispatch</div>
            <h2 className="font-heading font-black text-xl tracking-tight mt-1">Create Job</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 hover:bg-[#1a1a1a] flex items-center justify-center" data-testid="close-job-form">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Field label="Customer" required>
              <input
                type="text"
                value={form.customer}
                onChange={(e) => update("customer", e.target.value)}
                required
                className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                data-testid="form-customer"
              />
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field label="Customer Email (optional · for auto-POD receipt)">
              <input
                type="email"
                value={form.customer_email}
                onChange={(e) => update("customer_email", e.target.value)}
                placeholder="ops@customer.com"
                className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#22c55e] outline-none text-sm"
                data-testid="form-customer-email"
              />
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field label="Pickup Address" required>
              <input
                type="text"
                value={form.pickup_address}
                onChange={(e) => update("pickup_address", e.target.value)}
                required
                className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                data-testid="form-pickup"
              />
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field label="Dropoff Address" required>
              <input
                type="text"
                value={form.dropoff_address}
                onChange={(e) => update("dropoff_address", e.target.value)}
                required
                className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                data-testid="form-dropoff"
              />
            </Field>
          </div>

          <Field label="Scheduled">
            <input
              type="datetime-local"
              value={form.scheduled_at}
              onChange={(e) => update("scheduled_at", e.target.value)}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
              data-testid="form-scheduled"
            />
          </Field>

          <Field label="Priority">
            <LLSelect
              value={form.priority}
              onChange={(v) => update("priority", v)}
              testId="form-priority"
              className="!w-full text-sm normal-case tracking-normal"
              options={Object.entries(PRIORITY_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
            />
          </Field>

          <Field label="Assign Truck">
            <LLSelect
              value={form.asset_id || "__none__"}
              onChange={(v) => update("asset_id", v === "__none__" ? "" : v)}
              testId="form-asset"
              className="!w-full text-sm normal-case tracking-normal"
              options={[
                { value: "__none__", label: "— Unassigned —" },
                ...trucks.map((a) => ({ value: a.id, label: `${a.name} (${a.driver || "no driver"})` })),
              ]}
            />
          </Field>

          <Field label="Weight (lbs)">
            <input
              type="number"
              value={form.weight_lbs}
              onChange={(e) => update("weight_lbs", e.target.value)}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
              data-testid="form-weight"
            />
          </Field>

          <div className="md:col-span-2">
            <Field label="Notes">
              <textarea
                rows={3}
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm resize-none"
                data-testid="form-job-notes"
              />
            </Field>
          </div>

          <div className="md:col-span-2 flex items-center gap-3 pt-4 border-t border-[#2a2a2a]">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-[#4d4d4d] text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#1a1a1a]"
              data-testid="form-job-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-[#ff8c00] text-black hover:bg-[#e67e00] text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              data-testid="form-job-submit"
            >
              Dispatch Job
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1.5 block">
        {label} {required && <span className="text-[#ff8c00]">*</span>}
      </span>
      {children}
    </label>
  );
}
